<template>
  <ContentWrap>
    <el-form ref="queryFormRef" :inline="true" :model="queryParams" class="-mb-15px" label-width="90px">
      <el-form-item label="酒店" prop="shopId">
        <el-select v-model="queryParams.shopId" placeholder="全部酒店" clearable filterable class="!w-220px">
          <el-option v-for="item in hotels" :key="item.id" :label="item.name" :value="item.id" />
        </el-select>
      </el-form-item>
      <el-form-item label="业务类型" prop="businessType">
        <el-select v-model="queryParams.businessType" placeholder="全部" clearable class="!w-160px">
          <el-option label="支付入账" value="PAYMENT" />
          <el-option label="退款冲正" value="REFUND" />
          <el-option label="续住入账" value="EXTENSION" />
          <el-option label="钟点补款" value="HOURLY_SUPPLEMENT" />
        </el-select>
      </el-form-item>
      <el-form-item label="钱包阶段" prop="walletStage">
        <el-select v-model="queryParams.walletStage" placeholder="全部" clearable class="!w-160px">
          <el-option label="待结算" value="PENDING" />
          <el-option label="可用" value="USABLE" />
          <el-option label="自营内部" value="INTERNAL" />
        </el-select>
      </el-form-item>
      <el-form-item label="批次状态" prop="batchStatus">
        <el-select v-model="queryParams.batchStatus" placeholder="全部" clearable class="!w-160px">
          <el-option label="未批次" value="UNSETTLED" />
          <el-option label="已批次" value="SETTLED" />
          <el-option label="不适用" value="NOT_APPLICABLE" />
        </el-select>
      </el-form-item>
      <el-form-item label="创建时间" prop="createTimeRange">
        <el-date-picker
          v-model="queryParams.createTimeRange"
          type="datetimerange"
          value-format="YYYY-MM-DD HH:mm:ss"
          start-placeholder="开始时间"
          end-placeholder="结束时间"
          class="!w-360px"
        />
      </el-form-item>
      <el-form-item>
        <el-button type="primary" @click="handleQuery">
          <Icon class="mr-5px" icon="ep:search" />
          搜索
        </el-button>
        <el-button @click="resetQuery">
          <Icon class="mr-5px" icon="ep:refresh" />
          重置
        </el-button>
      </el-form-item>
    </el-form>
  </ContentWrap>

  <ContentWrap>
    <el-alert
      class="mb-16px"
      type="info"
      :closable="false"
      title="结算明细为订单级资金流水（幂等键 businessNo）：金额为正表示入账，为负表示退款冲正；「未批次」明细将在生成结算批次时归集。"
    />
    <el-table v-loading="loading" :data="list">
      <el-table-column
        :formatter="dateFormatter"
        label="创建时间"
        prop="createTime"
        width="175"
        align="center"
      />
      <el-table-column label="酒店" prop="shopName" min-width="160" show-overflow-tooltip>
        <template #default="scope">{{ scope.row.shopName || `酒店#${scope.row.shopId}` }}</template>
      </el-table-column>
      <el-table-column label="订单ID" prop="orderId" width="190" show-overflow-tooltip />
      <el-table-column label="业务单号" prop="businessNo" min-width="210" show-overflow-tooltip />
      <el-table-column label="业务类型" width="110" align="center">
        <template #default="scope">{{ businessTypeLabel(scope.row.businessType) }}</template>
      </el-table-column>
      <el-table-column label="运营模式" width="110" align="center">
        <template #default="scope">{{ operationModeLabel(scope.row.operationMode) }}</template>
      </el-table-column>
      <el-table-column label="业务金额" width="110" align="right">
        <template #default="scope">¥{{ money(scope.row.grossAmount) }}</template>
      </el-table-column>
      <el-table-column label="佣金比例" width="100" align="right">
        <template #default="scope">{{ percent(scope.row.commissionRate) }}</template>
      </el-table-column>
      <el-table-column label="平台佣金" width="110" align="right">
        <template #default="scope">¥{{ money(scope.row.platformAmount) }}</template>
      </el-table-column>
      <el-table-column label="商户到账" width="110" align="right">
        <template #default="scope">¥{{ money(scope.row.merchantAmount) }}</template>
      </el-table-column>
      <el-table-column label="钱包阶段" width="100" align="center">
        <template #default="scope">
          <el-tag :type="scope.row.walletStage === 'USABLE' ? 'success' : 'warning'" size="small">
            {{ walletStageLabel(scope.row.walletStage) }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="批次状态" width="100" align="center">
        <template #default="scope">{{ batchStatusLabel(scope.row.batchStatus) }}</template>
      </el-table-column>
    </el-table>
    <Pagination
      v-model:limit="queryParams.pageSize"
      v-model:page="queryParams.pageNo"
      :total="total"
      @pagination="getList"
    />
  </ContentWrap>
</template>

<script lang="ts" setup>
import { dateFormatter } from '@/utils/formatTime'
import * as SettlementApi from '@/api/hotel/settlement'

defineOptions({ name: 'HotelSettlementDetail' })

const loading = ref(true)
const list = ref<any[]>([])
const total = ref(0)
const hotels = ref<SettlementApi.HotelPlatformShopOption[]>([])
const queryFormRef = ref()

const queryParams = reactive({
  pageNo: 1,
  pageSize: 20,
  shopId: undefined as string | number | undefined,
  businessType: undefined as string | undefined,
  walletStage: undefined as string | undefined,
  batchStatus: undefined as string | undefined,
  createTimeRange: undefined as string[] | undefined
})

const money = (value: any) => Number(value || 0).toFixed(2)
const percent = (value: any) => (value == null ? '-' : `${Number(value).toFixed(2)}%`)
const businessTypeLabel = (type?: string) => {
  const labels: Record<string, string> = {
    PAYMENT: '支付入账',
    REFUND: '退款冲正',
    EXTENSION: '续住入账',
    HOURLY_SUPPLEMENT: '钟点补款'
  }
  return type ? labels[type] || type : '-'
}
const operationModeLabel = (mode?: string) => {
  const labels: Record<string, string> = {
    SELF_OPERATED: '自营',
    PLATFORM_MANAGED: '平台托管',
    THIRD_PARTY: '第三方入驻'
  }
  return mode ? labels[mode] || mode : '-'
}
const walletStageLabel = (stage?: string) => {
  const labels: Record<string, string> = { PENDING: '待结算', USABLE: '可用', INTERNAL: '自营内部' }
  return stage ? labels[stage] || stage : '-'
}
const batchStatusLabel = (status?: string) => {
  const labels: Record<string, string> = {
    UNSETTLED: '未批次',
    SETTLED: '已批次',
    NOT_APPLICABLE: '不适用'
  }
  return status ? labels[status] || status : '-'
}

const getList = async () => {
  loading.value = true
  try {
    const range = queryParams.createTimeRange || []
    const data = await SettlementApi.getSettlementDetailPage({
      pageNo: queryParams.pageNo,
      pageSize: queryParams.pageSize,
      shopId: queryParams.shopId,
      businessType: queryParams.businessType,
      walletStage: queryParams.walletStage,
      batchStatus: queryParams.batchStatus,
      beginCreateTime: range[0],
      endCreateTime: range[1]
    })
    list.value = data?.list || []
    total.value = data?.total || 0
  } finally {
    loading.value = false
  }
}

const handleQuery = () => {
  queryParams.pageNo = 1
  getList()
}

const resetQuery = () => {
  queryParams.shopId = undefined
  queryParams.businessType = undefined
  queryParams.walletStage = undefined
  queryParams.batchStatus = undefined
  queryParams.createTimeRange = undefined
  queryFormRef.value?.resetFields()
  handleQuery()
}

onMounted(async () => {
  getList()
  hotels.value = (await SettlementApi.getHotelOptions()) || []
})
</script>
